/**
 * Notifications API client
 */

import { getAPIClient } from './client';

export interface Notification {
  id: string;
  user_id: string;
  project_id?: string;
  tenant_id?: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  action_url?: string;
  context: Record<string, any>;
  channels_sent: string[];
  channels_delivered: string[];
  channels_failed: string[];
  read: boolean;
  read_at?: string;
  created_at: string;
  expires_at?: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  unread_count: number;
  page: number;
  page_size: number;
}

export interface NotificationListParams {
  read?: boolean;
  type?: string;
  project_id?: string;
  severity?: string;
  page?: number;
  page_size?: number;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  project_id?: string;
  channel_preferences: Record<string, any>;
  notification_preferences: Record<string, any>;
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_timezone: string;
  quiet_hours_override_critical: boolean;
  max_notifications_per_hour: number;
  max_notifications_per_day: number;
  digest_enabled: boolean;
  digest_frequency?: string;
  digest_time?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreferencesUpdate {
  channel_preferences?: Record<string, any>;
  notification_preferences?: Record<string, any>;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_timezone?: string;
  quiet_hours_override_critical?: boolean;
  max_notifications_per_hour?: number;
  max_notifications_per_day?: number;
  digest_enabled?: boolean;
  digest_frequency?: string;
  digest_time?: string;
  is_active?: boolean;
}

export interface UnreadCountResponse {
  unread_count: number;
}

/**
 * List user notifications
 */
export async function listNotifications(params?: NotificationListParams & { user_id?: string }): Promise<NotificationListResponse> {
  const client = getAPIClient();
  try {
    // Use trailing slash to match FastAPI route definition
    // client.get() returns the unwrapped body (response.data), so "response" here is already the list payload
    const raw = await client.get<NotificationListResponse>('/api/notifications/', { params });
    // Handle both: raw = body (from APIClient.get) or raw = { data, response } (if ever wrapped)
    const data: NotificationListResponse =
      raw && typeof raw === 'object' && Array.isArray((raw as any).notifications)
        ? (raw as NotificationListResponse)
        : (raw as any)?.data ?? (raw as any)?.response ?? null;
    if (!data || !Array.isArray(data.notifications)) {
      return { notifications: [], total: 0, unread_count: 0, page: (params?.page || 1), page_size: (params?.page_size || 10) };
    }
    
    return {
      notifications: data.notifications,
      total: typeof data.total === 'number' ? data.total : 0,
      unread_count: typeof data.unread_count === 'number' ? data.unread_count : 0,
      page: typeof data.page === 'number' ? data.page : (params?.page || 1),
      page_size: typeof data.page_size === 'number' ? data.page_size : (params?.page_size || 10),
    };
  } catch (error: any) {
    // Return empty result on error instead of throwing
    console.error('[notifications API] Error fetching notifications:', {
      error: error?.message || error,
      response: error?.response?.data,
      status: error?.response?.status,
      params,
    });
    return { notifications: [], total: 0, unread_count: 0, page: 1, page_size: 10 };
  }
}

/**
 * Get notification by ID
 */
export async function getNotification(notificationId: string, user_id?: string): Promise<Notification> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  const raw = await client.get<Notification>(`/api/notifications/${notificationId}`, { params });
  return (raw as any)?.data ?? (raw as any)?.response ?? raw;
}

/**
 * Mark notification as read
 */
export async function markNotificationAsRead(notificationId: string, user_id?: string): Promise<Notification> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  const raw = await client.patch<Notification>(`/api/notifications/${notificationId}/read`, {}, { params });
  return (raw as any)?.data ?? (raw as any)?.response ?? raw;
}

/**
 * Delete notification
 */
export async function deleteNotification(notificationId: string, user_id?: string): Promise<void> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  await client.delete(`/api/notifications/${notificationId}`, { params });
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsAsRead(user_id?: string): Promise<{ status: string; message: string; count: number }> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  const raw = await client.patch<{ status: string; message: string; count: number }>('/api/notifications/read-all', {}, { params });
  return (raw as any)?.data ?? (raw as any)?.response ?? raw;
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(user_id?: string): Promise<number> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  try {
    // client.get() returns the unwrapped body
    const raw = await client.get<UnreadCountResponse>('/api/notifications/unread-count', { params });
    const data = (raw as any)?.data ?? (raw as any)?.response ?? raw;
    const unreadCount = typeof (data as UnreadCountResponse)?.unread_count === 'number'
      ? (data as UnreadCountResponse).unread_count
      : 0;
    return unreadCount;
  } catch (error: any) {
    console.error('[notifications API] Error fetching unread count:', {
      error: error?.message || error,
      response: error?.response?.data,
      status: error?.response?.status,
      user_id,
    });
    return 0;
  }
}

/**
 * Get user notification preferences
 */
export async function getNotificationPreferences(projectId?: string): Promise<NotificationPreferences> {
  const client = getAPIClient();
  const url = projectId 
    ? `/api/notifications/preferences/${projectId}`
    : '/api/notifications/preferences';
  
  try {
    // client.get() returns the unwrapped body
    const raw = await client.get<NotificationPreferences>(url);
    const data = (raw as any)?.data ?? (raw as any)?.response ?? raw;
    if (!data || (typeof data !== 'object')) {
      throw new Error('No data returned from preferences endpoint');
    }
    if (!(data as NotificationPreferences).id && !(data as NotificationPreferences).user_id) {
      throw new Error('Invalid preferences response structure');
    }
    return data as NotificationPreferences;
  } catch (error: any) {
    console.error('[notifications API] Error fetching notification preferences:', {
      error: error?.message || error,
      response: error?.response?.data,
      status: error?.response?.status,
      url,
      projectId,
    });
    throw error;
  }
}

/**
 * Update user notification preferences
 */
export async function updateNotificationPreferences(
  preferences: NotificationPreferencesUpdate,
  projectId?: string
): Promise<NotificationPreferences> {
  const client = getAPIClient();
  const url = projectId 
    ? `/api/notifications/preferences/${projectId}`
    : '/api/notifications/preferences';
  const raw = await client.put<NotificationPreferences>(url, preferences);
  return (raw as any)?.data ?? (raw as any)?.response ?? raw;
}

/**
 * Test notification channels
 */
export interface NotificationTestEmailRequest {
  email: string;
  message?: string;
}

export interface TestSMSRequest {
  phone_number: string;
  message?: string;
}

export interface TestWhatsAppRequest {
  phone_number: string;
  message?: string;
}

export interface TestInAppRequest {
  message?: string;
}

export async function testEmailChannel(request: NotificationTestEmailRequest): Promise<{ status: string; message: string }> {
  const client = getAPIClient();
  const raw = await client.post<{ status: string; message: string }>('/api/notifications/test/email', request);
  return (raw as any)?.data ?? (raw as any)?.response ?? raw;
}

export async function testSMSChannel(request: TestSMSRequest): Promise<{ status: string; message: string; external_id?: string }> {
  const client = getAPIClient();
  const raw = await client.post('/api/notifications/test/sms', request);
  return (raw as any)?.data ?? (raw as any)?.response ?? raw;
}

export async function testWhatsAppChannel(request: TestWhatsAppRequest): Promise<{ status: string; message: string; external_id?: string }> {
  const client = getAPIClient();
  const raw = await client.post('/api/notifications/test/whatsapp', request);
  return (raw as any)?.data ?? (raw as any)?.response ?? raw;
}

export async function testInAppChannel(request: TestInAppRequest): Promise<{ status: string; message: string; notification_id: string }> {
  const client = getAPIClient();
  const raw = await client.post('/api/notifications/test/inapp', request);
  return (raw as any)?.data ?? (raw as any)?.response ?? raw;
}

export interface ChannelStatus {
  email: { enabled: boolean; provider: string; configured: boolean };
  sms: { enabled: boolean; provider: string; configured: boolean };
  whatsapp: { enabled: boolean; provider: string; configured: boolean };
  inapp: { enabled: boolean; provider: string; configured: boolean };
}

export async function getChannelStatus(): Promise<ChannelStatus> {
  const client = getAPIClient();
  const raw = await client.get<ChannelStatus>('/api/notifications/test/status');
  return (raw as any)?.data ?? (raw as any)?.response ?? raw;
}

